/**
 * **The game layer** of the stylesheet, plus the tiny DOM kit that goes with it.
 *
 * Both live here because both are leaves: viewModels.ts is the data contract,
 * style.ts is the presentation contract, and every panel imports only those
 * two. That keeps the module graph acyclic (uiRoot → panels → style, never
 * back) without inventing a seventh file.
 *
 * Design direction: terminal-adjacent. Monospace, dark, 1px borders, square
 * corners, no blur, no shadows that cost a compositor layer. The HUD is chrome;
 * the game is the picture (docs/01 §6.4). Everything colour-ish routes through
 * a CSS custom property so a biome grade can tint the whole UI by writing five
 * variables — see `UIRoot.setTint`.
 *
 * **What is no longer here** (docs/28). The tokens, the element defaults and
 * the nine reusable components moved to `src/ds/`, which is a design system and
 * not a game: it declares `--ui-accent`, it styles `.panel`, `.row`, `.bar`,
 * `.chip` and a key cap, and it has never heard of a weapon. This file is
 * everything above that seam — the HUD, the codex, the loadout panel, the map,
 * the vendor, the prologue, the run feed — and the rule for whether something
 * belongs here is whether its selector names a thing that only exists in this
 * game. `tests/designSystem.test.ts` checks it in both directions.
 *
 * `UI_CSS` is still the whole sheet, tokens first, so every gate that reads it
 * — the #52 placement gate, the #69 scale gate, the hand-card gates — sees
 * exactly what the browser sees.
 */

import { DS_CSS } from '../ds';
import { REGION_CSS } from './hudLayout';
import { UI_FS_BASE_PX } from './settingsModel';

const STYLE_ID = 'noitioidvania-ui-style';

/**
 * The glyph each drawn room kind wears on the map (#25).
 *
 * It lives beside the stylesheet rather than in `viewModels.ts` because the same
 * character is written twice — once as CSS `content` on `.room::after`, once
 * into the legend swatch — and the two must agree or the key stops being a key.
 * `normal` has none: the unmarked room is the default.
 */
export const MAP_KIND_GLYPH: Readonly<Record<string, string>> = {
  sanctum: '◆',
  rest: '+',
  surface: '↑',
  normal: '',
};

const GAME_CSS = /* css */ `
/* ---- the game's own channel colours ----------------------------------------
   Four names src/ds/tokens.ts deliberately does not have. They are declared
   on the same element as the design system's tokens, so they cascade, theme and
   grade exactly like the rest, and they are read only by rules in this file.

   This is the portability seam in four lines. A design system that knew what a
   hit point was would not be a design system; a game that had to invent a
   second theming mechanism for four colours would have two of everything. The
   seam is a *vocabulary* boundary, not a mechanism boundary. */
.ui-root {
  --ui-hp:        #c8453c;
  --ui-shield:    #6fa8d3;
  --ui-rhythm:    #e0a860;
  --ui-dash:      #9ad0e8;
  --ui-flight:    #b8e0a0;
}

/* ============================================================== HUD */

/* Placement is the region layer's business (ui/hudLayout.ts). Nothing below
   this line sets position or an edge offset — tests/hudLayout.test.ts asserts
   it, because an overlay that pins itself to a viewport edge has opted out of
   the only thing stopping it colliding with the next one (#52). */
/* The HUD's own leading, and the single biggest lever on how large it reads.
   .ui-root sets 1.35, which is a *prose* line-height — right for the codex and
   the loadout panel, far too loose for a nine-pixel caps label. Every HUD line
   was carrying about a third of its own height in empty space.
   It matters more since #128 than it did before it: reserving a slot means the
   resting state *is* the formerly-maximal one, so the sub-line's line box is no
   longer paid for only while you are swinging — it is paid for always. Setting
   the leading here rather than element by element is also what keeps the
   reservation honest, because min-height: 1lh is derived from it. The floor and
   the natural line box move together and neither can outgrow the other; a floor
   pinned to some smaller constant instead would let the box grow again on the
   first line of text, which is the whole of #128 coming back. */
.hud__c { display: flex; flex-direction: column; gap: calc(3px * var(--ui-u)); line-height: 1.15; }
.hud__c--tl { align-items: flex-start; }
/* The bottom band is one flex row, not three pinned corners — see the note in
   hud.ts. The min-width:0 on every child is what actually lets them shrink;
   without it flex items refuse to go below their content width and overflow
   the row instead of compressing. */
.hud__bottom {
  display: flex; gap: calc(10px * var(--ui-u)); width: 100%;
  /* wrap-reverse so an overflowing row stacks upward, away from the bottom
     edge, instead of pushing content off-screen. It also *inverts the cross
     axis*, so flex-start is what bottom-aligns the cards here — flex-end
     pins them to the top, which is the opposite of what it reads as. */
  flex-wrap: wrap-reverse;
  align-items: flex-start;
}
.hud__c--bl, .hud__c--bc, .hud__c--br { min-width: 0; }
/* The hand cards get a real flex-basis so they hold their comfortable size and
   the rhythm meter yields first. Basis 0 on the hands (the obvious choice)
   inverts the priority: the meter keeps its natural 280px and squashes both
   weapons to 60px, which is exactly backwards — the weapons are the read. */
.hud__c--bl { flex: 1 1 calc(190px * var(--ui-u)); align-items: flex-start; }
.hud__c--br { flex: 1 1 calc(190px * var(--ui-u)); align-items: flex-end; }
.hud__c--bc { flex: 0 1 auto; align-items: center; padding-bottom: 2px; }

@media (max-width: 700px) {
  /* No room for three abreast: the meter takes its own line above the hands.
     The order property puts it last so wrap-reverse floats it to the top. */
  .hud__c--bc { order: 1; flex: 1 0 100%; }
}

/* 190px is the comfortable width, not a floor. A hard width here put the vitals
   block 15px outside its own region at 540px viewport — the one collision a
   grid cannot prevent, content wider than the area it was given, and the check
   named it the first time it ran against the new layout. */
.hud__vit { display: flex; flex-direction: column; gap: calc(3px * var(--ui-u)); width: 100%; max-width: calc(190px * var(--ui-u)); }
/* Wrapping rather than clipping is the whole point of the scale setting: at
   Huge on a 540px window "VITALS" and "100/100" are wider than a third of the
   screen together, and the region clips what does not fit. A player who turned
   the text up to read it is the last person who should be shown half of it. */
.hud__hpline { display: flex; flex-wrap: wrap; justify-content: space-between; font-size: calc(10px * var(--ui-u)); letter-spacing: .14em; }
.hud__hp { height: round(nearest, calc(9px * var(--ui-u)), 1px); }
.hud__shield { height: round(nearest, calc(4px * var(--ui-u)), 1px); }
/* Issue #171. Same shape as .hud__shield — a thin bar under vitals — hidden
   entirely (via the "hidden" attribute, same as .hud__shield) rather than
   shown empty for a double-jump body with no meter. */
.hud__flight { height: round(nearest, calc(4px * var(--ui-u)), 1px); }

/* Statuses: shape first, colour second. Three at once must stay countable
   (docs/01 §6.4), so each is a bordered box with its own glyph and a stack
   count — never a full-body tint. */
.hud__statuses { display: flex; gap: calc(3px * var(--ui-u)); flex-wrap: wrap; max-width: min(calc(220px * var(--ui-u)), 100%); }
.status {
  position: relative;
  width: round(nearest, calc(20px * var(--ui-u)), 1px);
  height: round(nearest, calc(20px * var(--ui-u)), 1px);
  display: grid; place-items: center;
  border: 1px solid var(--ui-line-hot); background: var(--ui-cell);
  font-size: calc(12px * var(--ui-u)); line-height: 1;
}
.status[data-kind="buff"]    { border-color: var(--ui-good); color: var(--ui-good); }
.status[data-kind="debuff"]  { border-color: var(--ui-bad);  color: var(--ui-bad); }
.status[data-kind="neutral"] { border-color: var(--ui-fg-dim); color: var(--ui-fg); }
.status > b { position: absolute; right: -2px; bottom: -4px; font-size: calc(9px * var(--ui-u)); color: var(--ui-fg); background: var(--ui-well); padding: 0 1px; }
.status > u { position: absolute; left: 0; bottom: 0; height: round(nearest, calc(2px * var(--ui-u)), 1px); width: 100%; transform-origin: 0 50%; transform: scaleX(var(--f,1)); background: currentColor; }

/* Two currencies, one row, deliberately unlike each other. Scrip is run-scoped
   and dies with you, Ash banks forever — same styling on both is how a player
   learns to hoard the one that vanishes. Scrip gets the live accent and a
   bracket that reads as provisional; Ash is quiet and plain. */
.hud__purse { display: flex; flex-wrap: wrap; gap: calc(8px * var(--ui-u)); align-items: center; font-size: calc(10px * var(--ui-u)); letter-spacing: .1em; }
.purse { white-space: nowrap; }
.purse--scrip { color: var(--ui-accent); }
.purse--scrip::after { content: " run"; color: var(--ui-fg-mute); letter-spacing: .16em; }
.purse--ash { color: var(--ui-fg-dim); }
.purse--ash::after { content: " banked"; color: var(--ui-fg-mute); letter-spacing: .16em; }

.hud__dash { display: flex; gap: calc(3px * var(--ui-u)); }
.dashpip {
  width: round(nearest, calc(10px * var(--ui-u)), 1px);
  height: round(nearest, calc(10px * var(--ui-u)), 1px);
  border: 1px solid var(--ui-line-hot); background: var(--ui-well); position: relative;
}
.dashpip[data-full="1"] { background: var(--ui-dash); border-color: var(--ui-dash); }
.dashpip > i { position: absolute; inset: 0; transform-origin: 50% 100%; transform: scaleY(var(--f,0)); background: var(--ui-dash); opacity: .5; }

/* The ability strip (#129).
   One key cap, the glyphs of the abilities this run has found, and a count of
   the ones it has not. It used to be nine labelled chips — glyph, full name and
   a key hint each, unfound ones dimmed — wrapping across the whole width of the
   bottom-left corner, directly above the main-hand card. See the note on
   Hud.updateAbilities for why that is now four glyphs and a number.
   min-height keeps the strip's footprint when a run has found nothing, so
   picking up a first ability does not move the hand card (#128). */
.hud__abilities {
  display: flex; align-items: center; gap: calc(4px * var(--ui-u));
  min-height: round(nearest, calc(13px * var(--ui-u)), 1px);
}
/* The gap is 6px rather than 3 so the armed caret, which hangs outside its own
   box, lands in clear space instead of over the neighbour's border. */
.hud__abilityglyphs { display: flex; gap: calc(6px * var(--ui-u)); flex-wrap: wrap; }
/* The shared key, stated once. Same cell as the hand cards' key cap, because it
   is the same kind of thing — the button, not the verb. Both are filled from
   the live binding table now (#134); the hand cards used to read LMB/RMB, which
   is what the #128 measurement note further down still calls "the RMB cell". */
.ability__key {
  display: grid; place-items: center; flex: none;
  min-width: round(nearest, calc(13px * var(--ui-u)), 1px);
  height: round(nearest, calc(13px * var(--ui-u)), 1px);
  padding: 0 calc(2px * var(--ui-u));
  font-size: calc(9px * var(--ui-u)); color: var(--ui-fg-dim);
}
.ability__key:not(:empty) { border: 1px solid var(--ui-line); }
/* The whole of what is left of "nine dimmed rows": how many are still out
   there. The Metroidvania promise is one fact, so it gets one token. */
.ability__more { flex: none; font-size: calc(9px * var(--ui-u)); color: var(--ui-fg-mute); }
/* The plate is the point (#52). These sit over the brightest, busiest band of
   terrain in the game, and they were the only HUD element without an opaque
   backing — --ui-panel-2 is opaque, but see the armed rule below. The 0-blur
   text shadow costs no compositor layer and is what keeps the glyph legible
   where a chip straddles a light cell and a dark one. */
.ability {
  position: relative; display: grid; place-items: center;
  width: round(nearest, calc(13px * var(--ui-u)), 1px);
  height: round(nearest, calc(13px * var(--ui-u)), 1px);
  border: 1px solid var(--ui-line-hot); background: var(--ui-panel);
  font-size: calc(10px * var(--ui-u)); line-height: 1; white-space: nowrap;
  text-shadow: 0 1px 0 var(--ui-well);
}
/* Not-ready is dim, not invisible: at --ui-fg-mute on the panel this was under
   2.5:1 and unreadable before the terrain was even considered. The cooldown
   wipe below already carries "not yet" without the colour doing it. */
.ability[data-ready="0"] { color: var(--ui-fg-dim); }
/* Which one the press would actually fire is the only reading of this strip
   that is worth anything mid-fight (docs/02 §6.5). The caret is a ::before so
   arming costs one attribute flip, it sits outside the 14px box so it cannot
   crowd the glyph, and border and colour carry it together rather than colour
   doing it alone. */
.ability[data-armed="1"] { border-color: var(--ui-accent); color: var(--ui-accent); }
.ability[data-armed="1"]::before {
  content: "▸"; position: absolute; left: calc(-5px * var(--ui-u));
  font-size: calc(9px * var(--ui-u)); color: var(--ui-accent);
}
/* Charges ride the corner rather than taking a column: a countable digit on a
   14px box, which is the size docs/01 §6.4 says a glyph has to stay legible at. */
.ability > b {
  position: absolute; right: 0; top: calc(-2px * var(--ui-u));
  font-size: calc(8px * var(--ui-u)); font-weight: normal; line-height: 1;
  color: var(--ui-accent-2); text-shadow: 0 1px 0 var(--ui-well), 0 0 2px var(--ui-well);
}
/* Cooldown wipe, same transform-only trick as .bar — 0 is just-spent, 1 ready.
   1px rather than 2: on a box this size a 2px rule sat *inside* the glyph's own
   line box and read as a strikethrough rather than as a gauge. */
.ability > u {
  position: absolute; left: 0; bottom: 0;
  height: round(nearest, calc(1px * var(--ui-u)), 1px); width: 100%;
  text-decoration: none; transform-origin: 0 50%; transform: scaleX(var(--f,1));
  background: var(--ui-accent-2);
}

/* The gathering field (#122's readings, #129's space).
   Sits in the strip the ability row gave back, immediately above the hand card,
   because both of its readings are things you act on *now*: how far up the hold
   you are, and whether the field is dragging something that will kill you.
   Always in the flow, dimmed when shut, so opening a field moves nothing (#128). */
.gather {
  display: flex; align-items: center; gap: calc(4px * var(--ui-u));
  min-height: round(nearest, calc(9px * var(--ui-u)), 1px);
  max-width: 100%; min-width: 0;
  font-size: calc(9px * var(--ui-u)); letter-spacing: .1em;
}
.gather[data-on="0"] { visibility: hidden; }
.gather__glyph { flex: none; color: var(--ui-dash); }
/* Countable pips, one per hold tier, not a charge bar. A bar says "more"; the
   ladder is four named steps and the player is aiming at one of them. */
.gather__tier { display: flex; gap: calc(2px * var(--ui-u)); flex: none; }
.gather__tier > i {
  width: round(nearest, calc(5px * var(--ui-u)), 1px);
  height: round(nearest, calc(5px * var(--ui-u)), 1px);
  background: var(--ui-cell); border: 1px solid var(--ui-line);
}
.gather__tier > i[data-on="1"] { background: var(--ui-dash); border-color: var(--ui-dash); }
/* The safety half. Named, because "something hot" is not an instruction, and
   escalated by *shape* — one chevron per third of the radius crossed — because
   docs/01 §6.4 does not allow the read to be carried by colour alone. The
   colour is the second half of the same signal, never the whole of it. */
.gather__hazard {
  min-height: 1lh; min-width: 0; color: var(--ui-warn); white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.gather__hazard[data-near="2"] { color: var(--ui-bad); }
.gather__hazard[data-near="3"] { color: var(--ui-bad); }
.gather__hazard[data-near="1"]::after { content: " ◂"; }
.gather__hazard[data-near="2"]::after { content: " ◂◂"; }
.gather__hazard[data-near="3"]::after { content: " ◂◂◂"; }

/* Hand cards — both hands always visible, with combo state and recovery. */
/* Width is the comfortable target, not a floor: the card must be allowed below
   190px on a narrow viewport rather than forcing the row to overflow, so
   min-width goes to 0 and max-width keeps two cards inside the band. */
.hand {
  display: flex; align-items: stretch; gap: calc(6px * var(--ui-u));
  border: 1px solid var(--ui-line); background: rgb(from var(--ui-panel) r g b / .82);
  padding: calc(3px * var(--ui-u)) calc(6px * var(--ui-u));
  width: calc(190px * var(--ui-u)); min-width: 0; max-width: 100%;
}
.hand[data-armed="1"] { border-color: var(--ui-accent); }
/* Overheated is a refusal, not a level: the weapon will not fire until it
   cools, so it outranks the heat bar and takes the whole card. */
.hand[data-overheated="1"] { border-color: var(--ui-bad); }
.hand__reload { height: round(nearest, calc(3px * var(--ui-u)), 1px); }
.hand__reserve { height: round(nearest, calc(3px * var(--ui-u)), 1px); }
/* A bar row that has nothing to say keeps its 3px and draws nothing (#128).
   It used to leave the flow with [hidden], and .hand__bars is a *centred*
   column — so the frame a reload started, the recovery bar the player was
   reading slid 2.5px up to make room for it. Measured in Chrome: the off-hand
   recovery bar at y=446.5 idle, y=444.0 the instant the reload row appeared,
   inside a card whose own box never moved. visibility reserves the slot, which
   is the fix; hiding it more quietly is not. */
.hand__bars > .bar[data-off="1"] { visibility: hidden; }
/* The four reservoir kinds are four weapon identities (docs/04 §5) — the label
   is in the sub-line, the colour is the at-a-glance half of the same read. */
.hand__reserve[data-kind="fuel"]    > i { background: var(--ui-warn); }
.hand__reserve[data-kind="hunger"]  > i { background: var(--ui-r-cursed); }
.hand__reserve[data-kind="spin"]    > i { background: var(--ui-dash); }
.hand__reserve[data-kind="battery"] > i { background: var(--ui-accent-2); }
/* Guard is the shield's, and it is the only reservoir that reads down from full
   — it takes the shield colour so the bar that protects you looks like the
   shield bar and not like another resource you are building up. */
.hand__reserve[data-kind="guard"]   > i { background: var(--ui-shield); }
/* flex: none, for the same reason .hand__bars has it, and this is the one the
   issue is named after (#128). A width alone is only a *basis*: shrink defaults
   to 1, so the moment the sub-line's nowrap text outgrew the body's share of
   the card, flex took the difference out of these two. Measured in Chrome, off
   hand, before: the RMB cell sat at 20.76px with the magazine alone and dropped
   to 19.70px the frame a shot added the combo node to the line, the weapon
   glyph 20.63 -> 17.02, both springing back when the swing ended. The label the
   player is looking at is the last thing that should pay for a longer string —
   the sub-line already clips, which is what overflow: hidden on it is for. */
.hand__key { display: grid; place-items: center; flex: none; width: round(nearest, calc(22px * var(--ui-u)), 1px); border: 1px solid var(--ui-line); font-size: calc(9px * var(--ui-u)); color: var(--ui-fg-dim); }
.hand__glyph { display: grid; place-items: center; flex: none; width: round(nearest, calc(22px * var(--ui-u)), 1px); font-size: calc(13px * var(--ui-u)); }
.hand__body { display: flex; flex-direction: column; gap: calc(2px * var(--ui-u)); min-width: 0; flex: 1 1 auto; }
/* Weapon names and sub-lines must truncate rather than push the card wider —
   an overflowing card is what drove the whole bottom row into collision. */
.hand__name { font-size: calc(11px * var(--ui-u)); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* One line, reserved, whether or not there is anything to put in it (#128).
   handSubtitle() is the empty string while the hand is idle and carries the
   combo node from the first frame of a swing, so without a floor this line was
   0px at rest and 12px mid-swing: the main-hand card grew 31.84 -> 43.99 on
   every click and the dash pips and the ability row above it rode up with it.
   1lh is this element's own line box, so the floor cannot drift from the
   font-size beside it the way a second copy of the 1.35 would. */
.hand__sub {
  font-size: calc(9px * var(--ui-u)); color: var(--ui-fg-dim); display: flex; gap: calc(6px * var(--ui-u));
  overflow: hidden; white-space: nowrap; min-height: 1lh;
}
.hand__sub > * { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
/* min-height for the same reason .hand__sub has one (#128): a weapon with no
   combo string, and an empty hand, render no pips at all, and the row then
   contributes nothing and the card loses 3px. The pips are left-aligned, so
   the spare ones at the *end* may keep using [hidden] — dropping those out of
   the flow moves nothing. */
.hand__combo { display: flex; gap: calc(2px * var(--ui-u)); min-height: round(nearest, calc(3px * var(--ui-u)), 1px); }
.hand__combo > i {
  width: round(nearest, calc(8px * var(--ui-u)), 1px);
  height: round(nearest, calc(3px * var(--ui-u)), 1px);
  background: var(--ui-line-hot);
}
.hand__combo > i[data-on="1"] { background: var(--ui-accent); }
.hand__combo > i[data-spare="1"] { visibility: hidden; }
/* Fixed width and no shrink: left flexible, the bars collapse to ~2px and the
   recovery/charge readout silently disappears. */
.hand__bars { display: flex; flex-direction: column; gap: calc(2px * var(--ui-u)); flex: none; width: round(nearest, calc(30px * var(--ui-u)), 1px); justify-content: center; }
.hand .bar { height: round(nearest, calc(3px * var(--ui-u)), 1px); }
.hud__c--br .hand { flex-direction: row-reverse; }

/* Rhythm meter (docs/04 §8). Segmented so the tier is countable, with an
   explicit "which hand next" prompt — the meter is useless if the player
   can't act on it. */
.rhythm { display: flex; flex-direction: column; align-items: center; gap: calc(2px * var(--ui-u)); }
.rhythm__segs { display: flex; gap: calc(2px * var(--ui-u)); }
.rhythm__segs > i {
  width: round(nearest, calc(12px * var(--ui-u)), 1px);
  height: round(nearest, calc(7px * var(--ui-u)), 1px);
  background: var(--ui-cell); border: 1px solid var(--ui-line);
}
.rhythm__segs > i[data-on="1"] { background: var(--ui-rhythm); border-color: var(--ui-rhythm); }
.rhythm__meta { display: flex; gap: calc(8px * var(--ui-u)); font-size: calc(9px * var(--ui-u)); color: var(--ui-fg-dim); letter-spacing: .14em; }
.rhythm[data-decay="1"] .rhythm__segs > i[data-on="1"] { opacity: .45; }
.rhythm__next { color: var(--ui-accent); }

/* ============================================================== panels */

/* The browse list is now the left column and the build is the middle one. It
   still needs room for a name plus its action buttons, so it gets a basis
   rather than a hard width and yields to the build column when space is short. */
.col--list  { flex: 1 1 360px; max-width: 460px; border-right: 1px solid var(--ui-line); }
.col--right { width: 330px; border-left: 1px solid var(--ui-line); }

/* Build column: two hands, each with its sockets attached to it. Grouping the
   sockets under the hand they belong to is the whole point — a flat list of
   five sockets does not say which weapon they are on. */
.build { padding: 6px; display: flex; flex-direction: column; gap: 4px; }
.handbox { border: 1px solid var(--ui-line); background: var(--ui-panel-2); padding: 4px; display: flex; flex-direction: column; gap: 2px; }
.handbox[data-focus="1"] { border-color: var(--ui-accent); }
.handbox > .slot { background: transparent; border: none; padding: 1px 2px; }
.slot--socket .slot__kind { color: var(--ui-fg-dim); }
.slot--socket { margin-left: 12px; border-left: 1px solid var(--ui-line) !important; }
.filters { display: flex; flex-wrap: wrap; gap: 4px; padding: 5px 8px; border-bottom: 1px solid var(--ui-line); align-items: center; }
.filters select { max-width: 130px; }
.filters input[type="search"] { min-width: 170px; flex: 1 1 auto; }
.filters label { display: inline-flex; align-items: center; gap: 3px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); cursor: pointer; }

/* Rows. One delegated listener on the list, data-act on the buttons; the row
   itself is a design-system primitive (src/ds/primitives.ts) and only its
   game-specific columns are here. */
.row[data-equipped="1"] .row__name { color: var(--ui-accent); }
.row__star { border: none; background: none; padding: 0 2px; color: var(--ui-fg-mute); width: 16px; }
.row__star[data-on="1"] { color: var(--ui-accent); }
.row__name { width: 168px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row__meta { width: 96px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row__stat { width: 62px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); text-align: right; }
.row__tags { flex: 1 1 auto; min-width: 0; font-size: var(--ui-fs-label); color: var(--ui-fg-mute); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Detail / synergy pane */
.detail { padding: 8px; display: flex; flex-direction: column; gap: 8px; }
.detail__title { color: var(--ui-accent); text-transform: uppercase; letter-spacing: .12em; }
.detail__line { font-size: var(--ui-fs-label); color: var(--ui-fg-dim); }
.detail__prose { color: var(--ui-fg-dim); font-size: 11px; }

.syn { display: flex; flex-direction: column; gap: 6px; border: 1px solid var(--ui-line); padding: 6px; background: var(--ui-panel-2); }
.syn__group { display: flex; flex-direction: column; gap: 2px; }
.syn__lead { color: var(--ui-accent-2); font-size: var(--ui-fs-label); letter-spacing: .1em; }
.syn__lead::before { content: "▸ "; }
.syn__item { padding-left: 14px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); }
.syn__item b { color: var(--ui-fg); font-weight: normal; }
.syn__item i { color: var(--ui-fg-mute); font-style: normal; }
.syn__none { padding-left: 14px; font-size: var(--ui-fs-label); color: var(--ui-fg-mute); }
.syn__warn { color: var(--ui-warn); font-size: var(--ui-fs-label); }
.syn__warn::before { content: "! "; }

.slots { display: flex; flex-direction: column; gap: 3px; }
.slot { display: flex; align-items: center; gap: 6px; border: 1px solid var(--ui-line); padding: 2px 5px; background: var(--ui-panel-2); }
.slot[data-target="1"] { border-color: var(--ui-accent); }
.slot[data-empty="1"] .slot__name { color: var(--ui-fg-mute); }
.slot__kind { width: 58px; font-size: var(--ui-fs-tiny); letter-spacing: .12em; color: var(--ui-fg-mute); text-transform: uppercase; }
.slot__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; }
.slot button { font-size: var(--ui-fs-tiny); padding: 0 4px; }

/* Comparison tray — up to 3 columns, deltas colour-coded against column 0. */
/* The tray and the synergy block are the panel's spine (docs/04 §7.1), so it
   gets the space the six filter dropdowns used to occupy. */
.cmp { border-top: 1px solid var(--ui-line-hot); background: var(--ui-panel-2); max-height: 46%; display: flex; flex-direction: column; }
.cmp__head { display: flex; align-items: center; gap: 8px; padding: 3px 8px; border-bottom: 1px solid var(--ui-line); }
.cmp table { border-collapse: collapse; width: 100%; font-size: var(--ui-fs-label); }
.cmp th, .cmp td { padding: 1px 8px; text-align: left; border-bottom: 1px solid var(--ui-line-soft); white-space: nowrap; }
.cmp th { color: var(--ui-fg-mute); font-weight: normal; text-transform: uppercase; letter-spacing: .12em; }
.cmp td.k { color: var(--ui-fg-mute); width: 150px; }
.cmp td.v span.d { margin-left: 6px; }
.cmp td.v span.d[data-dir="1"]  { color: var(--ui-good); }
.cmp td.v span.d[data-dir="-1"] { color: var(--ui-bad); }
.cmp td.v[data-dir="1"]  { color: var(--ui-good); }
.cmp td.v[data-dir="-1"] { color: var(--ui-bad); }
.cmp__base { color: var(--ui-accent); }

/* Loadout save slots */
.lslot { display: flex; align-items: center; gap: 6px; padding: 4px 8px; border-bottom: 1px solid var(--ui-line-soft); }
.lslot__idx { width: 18px; color: var(--ui-fg-mute); }
.lslot input { width: 150px; }
.lslot__sum { flex: 1 1 auto; min-width: 0; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Radial quick-swap (hold-Tab) */
.radial { position: absolute; inset: 0; display: grid; place-items: center; }
.radial__disc { position: relative; width: 340px; height: 340px; border: 1px solid var(--ui-line-hot); border-radius: 50%; background: rgb(from var(--ui-bg) r g b / .86); }
.radial__hub { position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%); text-align: center; width: 150px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); }
.radial__item {
  position: absolute; width: 92px; margin-left: -46px; margin-top: -18px;
  border: 1px solid var(--ui-line); background: var(--ui-panel); padding: 2px 4px;
  text-align: center; font-size: var(--ui-fs-label);
}
.radial__item[data-on="1"] { border-color: var(--ui-accent); color: var(--ui-accent); background: var(--ui-panel-3); }
.radial__item[data-empty="1"] { color: var(--ui-fg-mute); }

/* ============================================================== codex */

.cx { display: flex; flex-wrap: wrap; gap: 4px; padding: 8px; align-content: flex-start; }
.mat { width: 128px; border: 1px solid var(--ui-line); background: var(--ui-panel-2); padding: 3px 5px; cursor: pointer; display: flex; align-items: center; gap: 5px; }
.mat:hover { border-color: var(--ui-line-hot); }
.mat[data-found="0"] { opacity: .35; cursor: default; }
.mat[data-on="1"] { border-color: var(--ui-accent); }
.mat__sw { width: 12px; height: 12px; border: 1px solid #000; flex: none; image-rendering: pixelated; }
.mat__n { font-size: var(--ui-fs-label); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.rx { display: flex; gap: 8px; padding: 6px 8px; border-bottom: 1px solid var(--ui-line-soft); }
.rx[data-found="0"] { opacity: .4; }
.rx__thumb { width: 64px; height: 64px; flex: none; border: 1px solid var(--ui-line); background: var(--ui-well); image-rendering: pixelated; display: block; }
.rx__b { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.rx__eq { color: var(--ui-fg); }
.rx__eq b { color: var(--ui-accent-2); font-weight: normal; }
.rx__eq i { color: var(--ui-accent); font-style: normal; }
.rx__meta { font-size: var(--ui-fs-label); color: var(--ui-fg-mute); }
.rx__ord { font-size: var(--ui-fs-label); color: var(--ui-fg-mute); width: 40px; flex: none; text-align: right; }
.rx[data-on="1"] { background: var(--ui-panel-2); box-shadow: var(--ui-mark); }
/* Reactant / reactant / product, when no frame was captured. The third band is
   twice the height so the outcome reads as the outcome, not as a third input. */
.rx__swatch { display: grid; grid-template-rows: 1fr 1fr 2fr; }
.rx__swatch > i { display: block; background: var(--ui-line-soft); }
.rx__swatch > i[data-any="1"] {
  background: repeating-linear-gradient(45deg, var(--ui-line-soft) 0 4px, #1d2029 4px 8px);
}
.enemy[data-on="1"] { background: var(--ui-panel-2); box-shadow: var(--ui-mark); }
.enemy { cursor: pointer; }

.enemy { padding: 6px 8px; border-bottom: 1px solid var(--ui-line-soft); display: flex; flex-direction: column; gap: 3px; }
.enemy[data-found="0"] { opacity: .4; }
.enemy__comp { display: flex; height: 8px; border: 1px solid var(--ui-line); }
.enemy__comp > i { height: 100%; }
.enemy__w { font-size: var(--ui-fs-label); color: var(--ui-fg-dim); }
.enemy__w b { color: var(--ui-good); font-weight: normal; }
.enemy__w.res b { color: var(--ui-bad); }

.track { padding: 8px; border-bottom: 1px solid var(--ui-line); display: flex; flex-direction: column; gap: 4px; }
.track__h { display: flex; align-items: baseline; gap: 8px; }
.track__n { color: var(--ui-accent); text-transform: uppercase; letter-spacing: .16em; }
.track__fed { font-size: var(--ui-fs-label); color: var(--ui-fg-mute); }
.track__unlocks { display: flex; flex-wrap: wrap; gap: 4px; }
.unlock { border: 1px solid var(--ui-line); padding: 1px 5px; font-size: var(--ui-fs-label); color: var(--ui-fg-mute); }
.unlock[data-on="1"] { color: var(--ui-good); border-color: var(--ui-good); }

/* ============================================================== prologue */

/* #22 / docs/08 §2. The prologue's readable half is deliberately almost nothing:
   a key cap and a verb, next to the thing they act on, gone the moment the
   player does it. Everything here is sized so that the *chamber* is what the
   player is looking at — this layer is a caption, never a screen. */
.pro { position: absolute; inset: 0; display: grid; place-items: center; align-content: center; gap: 14px; }
.pro__card { text-align: center; max-width: min(520px, 80vw); }
.pro__line { font-size: 15px; letter-spacing: .28em; text-transform: uppercase; color: var(--ui-fg); }
.pro__note { font-size: 11px; letter-spacing: .1em; color: var(--ui-fg-mute); margin-top: 4px; }

/* Prompts sit low, clear of the middle of the screen, because the thing being
   taught is in the world behind them and a prompt on top of it teaches nothing. */
.pro__prompts { position: absolute; left: 0; right: 0; bottom: 96px; display: flex; justify-content: center; gap: 18px; }
.pro__prompt { display: flex; align-items: center; gap: 6px; transition: opacity var(--ui-dur) var(--ui-ease); }
.pro__keys { display: inline-flex; gap: 3px; }
.pro__keys kbd {
  display: inline-grid; place-items: center; min-width: 20px; height: 20px; padding: 0 5px;
  border: 1px solid var(--ui-fg-dim); background: rgb(from var(--ui-panel) r g b / .9);
  font: inherit; font-size: var(--ui-fs-label); letter-spacing: .08em; color: var(--ui-fg);
}
.pro__verb { font-size: 11px; letter-spacing: .2em; text-transform: lowercase; color: var(--ui-fg-dim); }

/* Offered only on a profile that has already seen this once. Hold, not tap. */
.pro__skip { position: absolute; right: 14px; bottom: 14px; font-size: var(--ui-fs-tiny); letter-spacing: .16em; color: var(--ui-fg-mute); }
.pro__skip > i { display: block; height: 2px; margin-top: 3px; background: var(--ui-fg-dim); transform-origin: 0 50%; transform: scaleX(var(--f, 0)); }

/* ============================================================== factions */

/* One bar per opposed pair (#11, docs/05 §5). The pair shares a single signed
   axis, so the needle leaving one pole *is* it arriving at the other — six
   separate gauges would draw that as a coincidence rather than as the rule. */
.fx { padding: 8px; border-bottom: 1px solid var(--ui-line); display: flex; flex-direction: column; gap: 4px; }
.fx__head { display: flex; align-items: baseline; gap: 8px; }
.fx__axis { flex: 1 1 auto; text-align: center; color: var(--ui-fg-mute); }
.fx__pole { cursor: pointer; letter-spacing: .1em; color: var(--ui-fg-dim); }
.fx__pole:hover { color: var(--ui-fg); }
.fx__pole[data-on="1"] { color: var(--ui-accent); }
.fx__pole[data-side="neg"] { text-align: right; }

.fx__bar { position: relative; height: 10px; border: 1px solid var(--ui-line); background: var(--ui-well); }
/* Centre tick: without it "untouched" and "slightly one way" look identical. */
.fx__centre { position: absolute; left: 50%; top: 0; bottom: 0; width: 1px; background: var(--ui-line-hot); }
.fx__needle { position: absolute; top: -2px; bottom: -2px; width: 3px; margin-left: -1px; background: var(--ui-fg-dim); }
.fx__needle[data-side="pos"] { background: var(--ui-accent); }
.fx__needle[data-side="neg"] { background: var(--ui-accent-2); }

.fx__foot { display: flex; align-items: baseline; font-size: var(--ui-fs-label); }
/* Standing is a word first; the tone is the second, faster read (docs/01 §6.4). */
.fx__state[data-tone="good"] { color: var(--ui-good); }
.fx__state[data-tone="dim"]  { color: var(--ui-fg-dim); }
.fx__state[data-tone="warn"] { color: var(--ui-warn); }
.fx__state[data-tone="bad"]  { color: var(--ui-bad); }
.fx__state b { font-weight: normal; color: var(--ui-fg-mute); }
.fx__none { padding: 8px; color: var(--ui-fg-dim); font-size: 11px; border-bottom: 1px solid var(--ui-line); }

/* ============================================================== vendor */

/* The altar's shape, not a shop screen (docs/13 §5, #11): one thing, in the
   world, taken by standing at it. It shares the centre-prompt region with the
   altar and the ability cache — which used to mean "same magic anchor, and hope
   two of them are never live at once"; now it means the three of them stack. */
.vend {
  border: 1px solid var(--ui-line-hot); background: rgb(from var(--ui-panel) r g b / .94);
  padding: calc(6px * var(--ui-u)) calc(11px * var(--ui-u));
  min-width: calc(220px * var(--ui-u)); max-width: min(calc(360px * var(--ui-u)), 86vw);
  display: flex; flex-direction: column; gap: calc(2px * var(--ui-u)); font-size: calc(10px * var(--ui-u));
}
/* Out of range it is a name and a standing and nothing else. Reading the stock
   should cost a walk, which is what makes the vendor a place rather than a menu. */
.vend[data-range="0"] { border-color: var(--ui-line); opacity: .8; min-width: 0; }
.vend[data-open="0"] { border-color: var(--ui-bad); }
.vend__head { display: flex; align-items: baseline; gap: calc(8px * var(--ui-u)); }
.vend__name { color: var(--ui-accent); text-transform: uppercase; letter-spacing: .14em; }
.vend__purse { margin-left: auto; color: var(--ui-accent); white-space: nowrap; }
.vend__refusal { color: var(--ui-bad); }
.vend__row { display: flex; align-items: baseline; gap: calc(6px * var(--ui-u)); }
.vend__key { width: round(nearest, calc(12px * var(--ui-u)), 1px); color: var(--ui-fg-mute); }
.vend__label { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vend__price { color: var(--ui-accent); white-space: nowrap; }
/* An unaffordable line states its own shortfall rather than being greyed into
   ambiguity — "why not" is the only question a refused line raises. */
.vend__row[data-can="0"] .vend__label,
.vend__row[data-can="0"] .vend__price { color: var(--ui-fg-mute); }
.vend__why { color: var(--ui-fg-mute); }
.vend__row[data-on="1"] { box-shadow: var(--ui-mark); padding-left: 4px; margin-left: -4px; }
.vend__row[data-on="1"] .vend__label { color: var(--ui-fg); }
.vend__take { color: var(--ui-accent); margin-top: 3px; }
.vend__clock { color: var(--ui-fg-mute); }
/* A hostile faction's stall is still on the map, and so are they. */
.vend[data-hostile="1"] { border-color: var(--ui-bad); }

/* ============================================================== map / pause */

.map { position: relative; flex: 1 1 auto; margin: 8px; border: 1px solid var(--ui-line); background: var(--ui-well); overflow: hidden; }
.room {
  position: absolute; border: 1px solid var(--ui-line-hot); background: var(--ui-panel-2);
  display: grid; place-items: center; overflow: hidden;
  font-size: var(--ui-fs-tiny); line-height: 1; color: transparent;
}
.room[data-visited="0"] { opacity: .18; }

/* Room kinds — only what worldgen places (#25). There is no boss kind and no
   shop kind: both were styled here for a long time and neither has ever been
   generated, and a map that shows a boss room that is not there is worse than
   no map, because the player navigates by it.

   Shape first, colour second, the same rule the status boxes follow (docs/01
   §6.4): each kind gets a glyph as well as a border, so the three stay
   distinguishable to a player who cannot separate the hues. */
.room[data-kind="sanctum"] { border-color: var(--ui-accent); color: var(--ui-accent); }
.room[data-kind="sanctum"]::after { content: "◆"; }
.room[data-kind="rest"]    { border-color: var(--ui-good); color: var(--ui-good); }
.room[data-kind="rest"]::after { content: "+"; }
.room[data-kind="surface"] { border-color: var(--ui-accent-2); color: var(--ui-accent-2); }
.room[data-kind="surface"]::after { content: "↑"; }
/* An *inferred* sanctum — one you have not stood in, deduced from a visited
   neighbour — is the one thing on this map worth walking toward, so it is not
   dimmed as far as the rest of the dark. The sanctum is the guaranteed
   checkpoint at a band boundary; finding it is the map's whole job. */
.room[data-visited="0"][data-kind="sanctum"] { opacity: .45; }

/* Last, and that is load-bearing: these weigh the same as the kind rules above,
   so source order is the only thing that keeps "you are here" from being
   repainted by the kind of room you are standing in. */
.room[data-current="1"] { border-color: var(--ui-accent); background: var(--ui-panel-3); box-shadow: inset 0 0 0 1px var(--ui-accent); }

.map__legend { position: absolute; left: 0; bottom: 0; right: 0; flex-wrap: wrap; row-gap: 2px; }
.map__key { display: inline-flex; align-items: center; gap: 4px; }
.map__key > i { width: 9px; height: 9px; border: 1px solid currentColor; font-style: normal; font-size: 7px; line-height: 7px; text-align: center; }
.map__key[data-kind="sanctum"] { color: var(--ui-accent); }
.map__key[data-kind="rest"]    { color: var(--ui-good); }
.map__key[data-kind="surface"] { color: var(--ui-accent-2); }

.pause { position: absolute; inset: 0; display: grid; place-items: center; }
/* max-height + scroll rather than a smaller font: at Large and Huge interface
   scale the seven buttons and five track rows ran off the bottom of the
   viewport, and what fell off was Quit to title and Abandon run — the two
   entries you open the menu to reach. Shrinking the box would have undone the
   setting the player just chose. */
.pause__box { border: 1px solid var(--ui-line-hot); background: var(--ui-panel); padding: 18px 26px; display: flex; flex-direction: column; gap: 6px; min-width: 260px; max-height: 90vh; overflow-y: auto; }
.pause__box h1 { margin: 0 0 8px; font-size: 14px; letter-spacing: .4em; text-transform: uppercase; color: var(--ui-accent); font-weight: normal; }
.pause__box button { padding: 4px 8px; text-align: left; letter-spacing: .14em; text-transform: uppercase; font-size: 11px; }

/* ========================================================== the run feed */

/* Toasts and the run log are two tiers of one feed and share one region
   (top-right). They used to be two overlays in two corners, each with its own
   offsets and its own media query, and below 560px the second query dropped the
   toast stack exactly onto the first — measured at 540x900, 171x48 px of
   overlap, which is the collision #52 is named for.
   As flow siblings in a column they cannot overlap at any width, and the two
   media queries are gone with the two anchors that needed them. */
/* justify-content only bites when the stack has been squeezed to fit its
   region — six toasts at Huge on a 540px window are taller than the 46vh cap.
   Bottom-aligned, the toast that gets clipped is the oldest; left at the
   default it is the newest, which is the one the player is being told. */
.toasts { display: flex; flex-direction: column; justify-content: flex-end; gap: calc(4px * var(--ui-u)); align-items: flex-end; width: 100%; }
.toast {
  border: 1px solid var(--ui-line-hot); background: rgb(from var(--ui-panel) r g b / .92);
  padding: calc(3px * var(--ui-u)) calc(8px * var(--ui-u));
  font-size: calc(11px * var(--ui-u)); max-width: calc(320px * var(--ui-u));
  border-left-width: 3px;
  overflow-wrap: anywhere;
}
.toast[data-kind="discovery"] { border-left-color: var(--ui-accent-2); }
.toast[data-kind="unlock"]    { border-left-color: var(--ui-good); }
.toast[data-kind="warn"]      { border-left-color: var(--ui-bad); }
.toast > i { display: block; font-style: normal; font-size: calc(9px * var(--ui-u)); letter-spacing: .18em; text-transform: uppercase; color: var(--ui-fg-mute); }
/* The consequence line, quieter than the headline and allowed to wrap — a
   biome's ambient hazard is a sentence, not a label. */
.toast > em { display: block; font-style: normal; font-size: calc(10px * var(--ui-u)); color: var(--ui-fg-dim); white-space: normal; }

/* ====================================================== run-scoped overlays */

/* These four are mounted by main.ts, which used to give each one a
   "position: fixed" and a hand-picked corner. They are ordinary flow content
   inside their regions now; all that is left here is how they look. */

/* The quiet tier of the run feed, directly under the toasts and in the same
   column. Right-aligned to match them, and ellipsised rather than wrapped —
   a material line is a headline, and a wrapped one costs two rows of the
   region for the same sentence. */
.ovl-log {
  width: 100%; text-align: right;
  font-size: calc(10px * var(--ui-u)); line-height: 1.55; letter-spacing: .06em;
  color: var(--ui-fg-dim); text-shadow: 0 1px 0 var(--ui-well);
}
.ovl-log > div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Control hints. Centred in the top band — the one placement that was tried and
   rejected before the regions existed, because centred *across the whole
   viewport* it ran into the vitals. The band is the middle column, so it now
   cannot reach either corner. */
.ovl-hints {
  text-align: center; font-size: calc(10px * var(--ui-u)); line-height: 1.7; letter-spacing: .08em;
  color: var(--ui-fg-mute);
}
.ovl-hints b { font-weight: normal; color: var(--ui-fg-dim); }

/* F3 debug readout. DOM rather than GL so it survives a renderer failure, which
   is exactly when you most want to read it. */
.ovl-debug {
  margin: 0; padding: calc(8px * var(--ui-u)) calc(10px * var(--ui-u));
  font: calc(11px * var(--ui-u))/1.45 var(--ui-font); white-space: pre;
  color: #9fe8b0; background: rgba(6,8,10,.82); border: 1px solid #1e2a22;
  max-width: 100%; overflow: hidden;
}

/* Altar and ability-cache prompts. Both sit in centre-prompt with the vendor,
   stacked — they used to share one magic "bottom: 132px" anchor, which meant an
   altar and a cache in the same room printed one through the other. */
.ovl-prompt { gap: calc(8px * var(--ui-u)); font-size: calc(10px * var(--ui-u)); line-height: 1.5; letter-spacing: .06em; max-width: 100%; }
${REGION_CSS}`;

/**
 * The whole sheet the browser sees: the design system, then this game.
 *
 * The order is the cascade and the seam at once. `DS_CSS` declares the tokens
 * before anything spends them and styles the nine primitives before this file
 * specialises them, so a game rule at equal specificity wins simply by being
 * later — which is what makes the game layer able to override the system
 * without `!important` and without raising its specificity.
 *
 * It stays one exported string because six gates read it as one — the #52
 * placement gate, the #69 scale gate and the hand-card box gates all scan
 * `UI_CSS` — and a design system that made those gates read three strings
 * would have moved the risk rather than removed it.
 */
export const UI_CSS = DS_CSS + GAME_CSS;

/** Idempotent — mounting a second UIRoot must not duplicate the sheet. */
export function injectStyle(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const s = doc.createElement('style');
  s.id = STYLE_ID;
  s.textContent = UI_CSS;
  doc.head.appendChild(s);
}

export function removeStyle(doc: Document = document): void {
  doc.getElementById(STYLE_ID)?.remove();
}

// ------------------------------------------------------------------ DOM kit

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function btn(label: string, act: string, cls = ''): HTMLButtonElement {
  const b = el('button', cls, label);
  b.type = 'button';
  b.dataset.act = act;
  return b;
}

export function clear(e: HTMLElement): void {
  // Faster than innerHTML='' and does not re-parse.
  while (e.firstChild) e.removeChild(e.firstChild);
}

/**
 * Write-on-change text binding. The HUD runs every frame inside a 0.5 ms budget
 * (docs/01 §9); assigning textContent unconditionally forces style+layout work
 * even when the string is identical, so every per-frame write goes through one
 * of these.
 */
export class TextBind {
  private last = '\u0000';
  constructor(readonly el: HTMLElement) {}
  set(v: string): void {
    if (v === this.last) return;
    this.last = v;
    this.el.textContent = v;
  }
}

/** Number binding: formats only when the numeric value actually moved. */
export class NumBind {
  private last = NaN;
  constructor(
    readonly el: HTMLElement,
    private fmt: (n: number) => string,
  ) {}
  set(v: number): void {
    if (v === this.last) return;
    this.last = v;
    this.el.textContent = this.fmt(v);
  }
}

/** Bar fill, quantised so sub-pixel jitter does not churn the style attribute. */
export class FillBind {
  private last = NaN;
  constructor(
    readonly el: HTMLElement,
    private steps = 200,
  ) {}
  set(v01: number): void {
    const q = Math.round(Math.max(0, Math.min(1, v01)) * this.steps) / this.steps;
    if (q === this.last) return;
    this.last = q;
    // Allocates one short string, but only on an actual change — a still HUD
    // costs nothing.
    this.el.style.setProperty('--f', String(q));
  }
}

export class AttrBind {
  private last = '\u0000';
  constructor(
    readonly el: HTMLElement,
    private name: string,
  ) {}
  set(v: string): void {
    if (v === this.last) return;
    this.last = v;
    this.el.setAttribute(this.name, v);
  }
}

export class ClassBind {
  private last: boolean | null = null;
  constructor(
    readonly el: HTMLElement,
    private cls: string,
  ) {}
  set(on: boolean): void {
    if (on === this.last) return;
    this.last = on;
    this.el.classList.toggle(this.cls, on);
  }
}

/**
 * Recycling element pool. Panels and the HUD both render variable-length lists
 * (statuses, dash pips, rows); allocating elements per frame or per rebuild is
 * the classic way to blow both the frame budget and the GC headroom.
 */
export class Pool<T extends HTMLElement> {
  private items: T[] = [];
  private used = 0;

  constructor(
    private host: HTMLElement,
    private make: () => T,
  ) {}

  begin(): void {
    this.used = 0;
  }

  next(): T {
    let it = this.items[this.used];
    if (!it) {
      it = this.make();
      this.items.push(it);
      this.host.appendChild(it);
    }
    if (it.hidden) it.hidden = false;
    this.used++;
    return it;
  }

  end(): void {
    for (let i = this.used; i < this.items.length; i++) {
      const it = this.items[i];
      if (!it.hidden) it.hidden = true;
    }
  }

  get length(): number {
    return this.used;
  }

  at(i: number): T | undefined {
    return this.items[i];
  }

  dispose(): void {
    for (const it of this.items) it.remove();
    this.items.length = 0;
    this.used = 0;
  }
}
